/**
 * KikPopup — product-area modal helper.
 * Moves/mounts popup content onto document.body so it escapes
 * stacking contexts (e.g. product main z-index < header).
 *
 * Usage:
 *   KikPopup.open('size')
 *   KikPopup.close('size')
 *   KikPopup.getOrCreate('size').toggle()
 *
 * Content sources: existing `.kik_size_guide[data-popup="…"]` in the page,
 * or pass `{ content }` when constructing manually.
 */
(function (global) {
  'use strict';

  var registry = new Map();

  function escapeAttr(value) {
    if (value == null) return '';
    if (global.CSS && typeof global.CSS.escape === 'function') {
      return global.CSS.escape(String(value));
    }
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function anyOpen() {
    var open = false;
    registry.forEach(function (instance) {
      if (instance.isOpen) open = true;
    });
    return open;
  }

  function KikPopup(options) {
    options = options || {};
    this.id = options.id || '';
    this.source = options.source || null;
    this.content = options.content != null ? options.content : null;
    this.className = options.className || '';
    this.el = null;
    this.isOpen = false;
    this._onKeydown = this._onKeydown.bind(this);
  }

  KikPopup.getOrCreate = function (id, options) {
    if (!id) return null;
    if (registry.has(id)) return registry.get(id);

    options = options || {};
    var source =
      options.source ||
      document.querySelector('.kik_size_guide[data-popup="' + escapeAttr(id) + '"]');

    if (!source && options.content == null) return null;

    var instance = new KikPopup({
      id: id,
      source: source,
      content: options.content,
      className: options.className || '',
    });
    registry.set(id, instance);
    return instance;
  };

  KikPopup.open = function (id, options) {
    var instance = KikPopup.getOrCreate(id, options);
    if (!instance) return null;
    instance.open();
    return instance;
  };

  KikPopup.close = function (id) {
    var instance = registry.get(id);
    if (instance) instance.close();
  };

  KikPopup.closeAll = function () {
    registry.forEach(function (instance) {
      if (instance.isOpen) instance.close();
    });
  };

  KikPopup.get = function (id) {
    return registry.get(id) || null;
  };

  KikPopup.prototype.mount = function () {
    if (this.el) return this.el;

    if (this.source) {
      this.el = this.source;
    } else {
      this.el = document.createElement('div');
      this.el.className = ('kik_size_guide text-custom kik-popup ' + this.className).trim();
      if (this.id) this.el.setAttribute('data-popup', this.id);

      if (typeof this.content === 'string') {
        this.el.innerHTML = this.content;
      } else if (this.content instanceof global.HTMLElement) {
        this.el.appendChild(this.content);
      }
    }

    if (this.el.parentElement !== document.body) {
      document.body.appendChild(this.el);
    }

    this.el.dataset.kikPopupMounted = 'true';
    this.el.classList.add('kik-popup--ported');
    return this.el;
  };

  KikPopup.prototype.open = function () {
    this.mount();
    this.el.classList.add('active');
    this.isOpen = true;
    document.body.classList.add('kik-popup-open');
    document.addEventListener('keydown', this._onKeydown);
    this.el.dispatchEvent(
      new CustomEvent('kik-popup:open', { bubbles: true, detail: { id: this.id } })
    );
    return this;
  };

  KikPopup.prototype.close = function () {
    if (!this.el) return this;
    this.el.classList.remove('active');
    this.isOpen = false;
    document.removeEventListener('keydown', this._onKeydown);
    if (!anyOpen()) {
      document.body.classList.remove('kik-popup-open');
    }
    this.el.dispatchEvent(
      new CustomEvent('kik-popup:close', { bubbles: true, detail: { id: this.id } })
    );
    return this;
  };

  KikPopup.prototype.toggle = function () {
    if (this.isOpen) this.close();
    else this.open();
    return this;
  };

  KikPopup.prototype.destroy = function () {
    this.close();
    if (this.el && this.el.parentElement) {
      this.el.parentElement.removeChild(this.el);
    }
    this.el = null;
    this.source = null;
    registry.delete(this.id);
  };

  KikPopup.prototype._onKeydown = function (event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      this.close();
    }
  };

  global.KikPopup = KikPopup;
})(typeof window !== 'undefined' ? window : this);
